import { useState, useEffect, createContext, useContext } from "react";
import { Header } from "./components/Header";
import { SubmitModal } from "./components/SubmitModal";
import { PromptSection } from "./components/PromptSection";
import { StoryPage } from "./components/StoryPage";
import { AdminPanel } from "./components/AdminPanel";
import "./index.css";

interface User {
  id: number;
  username: string;
  profile_pic_url: string | null;
}

interface Submission {
  id: number;
  prompt_id: number;
  user_id: number;
  title: string;
  body: string;
  word_count: number;
  created_at: string;
  username: string;
  profile_pic_url: string | null;
  like_count: number;
}

interface Prompt {
  id: number;
  text: string;
  created_at: string;
  closes_at: string | null;
  submissions: Submission[];
}

interface AppContextType {
  user: User | null;
  likes: number[];
  setLikes: (likes: number[]) => void;
  refreshData: () => void;
  isAdmin: boolean;
}

export const AppContext = createContext<AppContextType>({
  user: null,
  likes: [],
  setLikes: () => {},
  refreshData: () => {},
  isAdmin: false,
});

export function useAppContext() {
  return useContext(AppContext);
}

function getRoute(): { page: "home" | "story"; storyId?: number } {
  const path = window.location.pathname;
  const storyMatch = path.match(/^\/story\/(\d+)/);
  if (storyMatch) {
    return { page: "story", storyId: parseInt(storyMatch[1]) };
  }
  return { page: "home" };
}

function getInitialTheme(): "light" | "dark" {
  const stored = localStorage.getItem("borges-theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [likes, setLikes] = useState<number[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [route, setRoute] = useState(getRoute);
  const [theme, setTheme] = useState<"light" | "dark">(getInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("borges-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const fetchData = async () => {
    try {
      const [meRes, promptsRes] = await Promise.all([
        fetch("/api/me"),
        fetch("/api/prompts"),
      ]);
      const meData = await meRes.json();
      const promptsData = await promptsRes.json();

      setUser(meData.user);
      setLikes(meData.likes || []);
      setIsAdmin(meData.isAdmin || false);
      setPrompts(promptsData);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setRoute(getRoute());
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, "", path);
    setRoute(getRoute());
  };

  const handleSubmitClick = () => {
    if (!user) {
      window.location.href = "/auth/login";
      return;
    }
    setShowSubmitModal(true);
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const latestPrompt = prompts[0];

  return (
    <AppContext.Provider value={{ user, likes, setLikes, refreshData: fetchData, isAdmin }}>
      <div className="app">
        <Header
          user={user}
          theme={theme}
          onToggleTheme={toggleTheme}
          isAdmin={isAdmin}
          onAdminClick={() => setShowAdminPanel(true)}
        />

        {route.page === "story" && route.storyId ? (
          <StoryPage
            storyId={route.storyId}
            onBack={() => navigate("/")}
          />
        ) : (
          <main className="main-content">
            <p className="intro-text">
              Twice a month, users can submit their own Borges style stories based on a prompt.
              Login with your X account to submit. You can write on your own or with LLM assistance.
            </p>

            {latestPrompt && (
              <div className="latest-prompt-section">
                <div className="prompt-left">
                  <h2 className="section-label">Latest Prompt</h2>
                  <p className="prompt-text">"{latestPrompt.text}"</p>
                  <button className="submit-btn" onClick={handleSubmitClick}>
                    submit
                  </button>
                </div>
                <div className="prompt-right">
                  <h2 className="section-label">Submissions</h2>
                  <PromptSection
                    submissions={latestPrompt.submissions}
                    onStoryClick={(id) => navigate(`/story/${id}`)}
                  />
                </div>
              </div>
            )}

            {prompts.slice(1).map((prompt) => (
              <div key={prompt.id} className="prompt-section">
                <div className="prompt-left">
                  <h2 className="section-label">
                    {new Date(prompt.created_at).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "2-digit",
                    })}{" "}
                    Prompt
                  </h2>
                  <p className="prompt-text">"{prompt.text}"</p>
                </div>
                <div className="prompt-right">
                  <h2 className="section-label">Submissions</h2>
                  <PromptSection
                    submissions={prompt.submissions}
                    onStoryClick={(id) => navigate(`/story/${id}`)}
                  />
                </div>
              </div>
            ))}
          </main>
        )}

        {showSubmitModal && latestPrompt && (
          <SubmitModal
            promptId={latestPrompt.id}
            onClose={() => setShowSubmitModal(false)}
            onSuccess={() => {
              setShowSubmitModal(false);
              fetchData();
            }}
          />
        )}

        {showAdminPanel && (
          <AdminPanel
            prompts={prompts}
            onClose={() => setShowAdminPanel(false)}
          />
        )}
      </div>
    </AppContext.Provider>
  );
}
